/**
 * 画面に表示する文言（日本語）。文言はコードから分離し、将来の英語対応に備える（NFR-I18N-001）。
 */
export const ja = {
  appName: 'Roller Coaster Studio',
  untitledProject: '無題のプロジェクト',
  toolbar: {
    save: '保存',
    export: '書き出し',
    import: '読み込み',
    undo: '元に戻す',
    redo: 'やり直す',
    modeGroup: 'モード',
    modeEdit: '編集',
    modeRide: '走行',
    testRide: '試乗',
    fullscreen: '全画面',
    exitFullscreen: '全画面を終了',
  },
  storage: {
    saved: '保存済み',
    unsaved: '未保存の変更があります',
    saving: '保存しています…',
    saveFailed: '保存できませんでした。もう一度お試しください。',
    restoreFailed: '前回のプロジェクトを読み込めませんでした（サンプルを表示しています）：',
    leaveWarning: '保存していない変更があります。このページを離れますか？',
  },
  transfer: {
    importFailedTitle: 'ファイルを読み込めませんでした',
    importFailedBody: '現在のプロジェクトは変更していません。',
    dismiss: '閉じる',
    confirmReplace: '現在のプロジェクトを、読み込んだファイルで置き換えます。よろしいですか？',
  },
  contextMenu: {
    label: '制御点のメニュー',
    addAfter: 'この点の後ろに追加',
    remove: 'この点を削除',
    close: '閉じる',
  },
  panels: {
    objects: 'コース・制御点',
    properties: 'プロパティ',
    analysis: '解析',
    comingSoon: '準備中です。',
    objectsHint: 'コースの制御点の一覧です。選択すると、3D ビューでも強調されます。',
    propertiesHint: '選択した制御点の位置・バンク角・区間種別を編集できます。',
    analysisHint: '距離ごとの速度・高度・G のグラフを表示します。点線の縦線が再生位置です。',
  },
  viewport: {
    label: '3D ビュー',
    initializing: '3D ビューを準備しています…',
    unsupportedTitle: 'この環境では 3D 表示を利用できません',
    unsupportedBody:
      'WebGL2 に対応したブラウザ（iPad の Safari、最新の Chrome・Edge・Safari）でお試しください。',
    contextLost: '描画が一時停止しました。復帰を待っています…',
  },
  properties: {
    title: '制御点のプロパティ',
    noSelection: '制御点を選択すると、位置・バンク角・区間種別を編集できます。',
    closed: '周回コース（閉ループ）',
    closedNeedsThree: '周回コースにするには、制御点が 3 点以上必要です。',
    axes: { x: 'X（東西）', y: 'Y（高さ）', z: 'Z（南北）' },
    bank: 'バンク角',
    segment: '区間種別',
    segments: {
      normal: '通常',
      lift: 'リフト（チェーン）',
      launch: '発進加速（ローンチ）',
      brake: 'ブレーキ',
      station: '駅',
      block: 'ブロック',
    },
    params: {
      liftSpeed: '巻き上げ速度',
      maxDeceleration: '最大減速度',
      targetSpeed: '目標速度（0 = 停止）',
      dispatchSpeed: '送り出し速度',
      launchTargetSpeed: '到達速度',
      maxAcceleration: '加速度',
    },
    notANumber: '数値を入力してください。',
    notFound: '制御点が見つかりません。',
  },
  move: {
    title: '移動の設定',
    constraintGroup: '移動の制約',
    constraints: {
      free: '制限なし',
      horizontal: '水平面',
      x: 'X 軸',
      y: '垂直（Y）',
      z: 'Z 軸',
    },
    snap: 'グリッドスナップ',
    grid: '間隔',
  },
  rideSection: {
    label: 'いまの区間',
    station: '駅（ホーム）',
    lift: 'チェーンで巻き上げ中',
    launch: '発進加速中',
    brake: 'ブレーキ区間',
  },
  segmentLegend: {
    label: '区間の色分け',
    kinds: {
      station: '駅',
      lift: 'リフト（チェーン）',
      launch: '発進加速',
      brake: 'ブレーキ',
    },
  },
  vehicle: {
    title: '車両',
    select: '乗る車両',
    facts: '総質量 {mass} t・全長 {length} m・定員 {capacity} 人・最高 {speed} km/h',
  },
  environment: {
    title: '背景',
    template: '背景',
    custom: '自分で組み合わせ',
    sky: '空',
    ground: '地面',
    light: '光（時間帯）',
    customLight: '太陽を自分で決める',
    azimuth: '太陽の方角',
    elevation: '太陽の高さ',
    intensity: '光の強さ',
    sunFacts: '太陽は{compass}（{azimuth}°）、高さ {elevation}°',
    physics: '物理（重力・空気）',
    facts: '重力 {gravity} m/s²・空気密度 {air} kg/m³',
  },
  courses: {
    title: 'コース',
    select: '編集するコース',
    name: 'コース名',
    actions: 'コースの追加・複製・削除',
    add: '新規',
    duplicate: '複製',
    remove: '削除',
    addTitle: 'スタートから停止まで走る雛形から、新しいコースを作ります',
    confirmRemove: 'コース「{name}」を削除します。よろしいですか？',
    nameInvalid: 'コース名は 1〜100 文字で入力してください。',
    count: '{count} 本',
    addSample: 'サンプルから追加',
    addSampleTitle:
      'サンプルのコース（実在のコースターを真似たものを含む）を、このプロジェクトに追加します',
    addSamplePlaceholder: '（選ぶと追加します）',
  },
  controlPoints: {
    label: '制御点の一覧',
    point: '制御点',
    selected: '選択中',
    hint: '3D ビューで制御点をタップすると選択、ドラッグすると移動できます。',
    actions: '制御点の追加・削除',
    add: '追加',
    remove: '削除',
    addAtEnd: '末尾に制御点を追加します',
    addAfterSelected: '選択中の制御点の次に追加します',
  },
  safety: {
    title: '安全性・快適性の評価（参考値）',
    levels: { info: '情報', caution: '注意', warning: '警告', blocking: '続行不可' },
    icons: { info: 'ⓘ', caution: '▲', warning: '⚠', blocking: '⛔' },
    clean: '上限を超える区間も、失速もありません。',
    empty: '走行の結果を計算しています…',
    // {s}：弧長 [m]、{t}：時刻 [s]、{from}・{to}：区間の弧長 [m]、{peak}：最大値、{limit}：上限
    messages: {
      stall: '失速：{s} m 地点（{t} 秒）で速度が 0 になり、逆走します。',
      'speed-high':
        '速度が車両の最高速度を超えています：{from}〜{to} m（最大 {peak} km/h、上限 {limit} km/h）。',
      'vertical-g-high':
        '上下 G が上限を超えています：{from}〜{to} m（最大 {peak} G、上限 {limit} G）。',
      'vertical-g-low':
        '浮き上がり（負の G）が下限を超えています：{from}〜{to} m（最大 {peak} G、下限 {limit} G）。',
      'lateral-g': '横 G が上限を超えています：{from}〜{to} m（最大 {peak} G、上限 {limit} G）。',
      'numerical-error': '計算が不安定になりました：{s} m 付近で走行を止めました。',
      finished: '最後まで走行できました（{s} m 地点）。',
    },
  },
  charts: {
    label: '距離ごとのグラフ',
    empty: '走行の結果を計算しています…',
    speed: '距離 – 速度',
    altitude: '距離 – 高度',
    verticalG: '距離 – 上下 G',
    distanceAxis: '距離 [m]',
    speedAxis: '[km/h]',
    altitudeAxis: '[m]',
    gAxis: '[G]',
  },
  hud: {
    label: '走行中の数値（HUD）',
    time: '経過時間',
    distance: '走行距離',
    speed: '速度',
    altitude: '高度',
    verticalG: '上下 G',
    detail: '詳細',
    debug: 'デバッグ',
    maxSpeed: '最高速度',
    grade: '勾配',
    bank: 'バンク角',
    longitudinalG: '前後 G',
    lateralG: '横 G',
    combinedG: '合成 G',
    segment: '現在区間',
    segmentNumber: '区間',
    fps: '描画',
    computeTime: '物理計算',
    engine: '物理エンジン',
  },
  camera: {
    group: '視点',
    views: {
      overview: '全体俯瞰',
      'follow-behind': '追従（後方）',
      'follow-front': '追従（前方）',
      'follow-side': '追従（側方）',
      passenger: '乗客視点',
    },
    lookHint: 'ドラッグで見回し、ダブルタップで正面へ戻します。',
    presetsGroup: '全体俯瞰のプリセット',
    presets: { top: '真上', front: '正面', side: '側面', oblique: '斜め' },
  },
  settings: {
    quality: '描画品質',
    qualityOptions: { low: '低', medium: '中', high: '高' },
  },
  playback: {
    group: '再生の操作',
    play: '再生',
    pause: '一時停止',
    stop: '停止',
    rate: '再生速度',
    elapsed: '経過',
    ended: '終了',
  },
  windows: {
    move: (name: string) => `${name}を移動（ドラッグ・矢印キー）`,
    close: (name: string) => `${name}を閉じる`,
    reset: '配置を戻す',
    resetHint: '動かしたウィンドウを、元の位置に戻します（この端末に保存した配置）',
    menu: '全画面の操作',
    show: '表示するウィンドウ',
    overlays: '3D の上の表示',
    hideAll: 'すっきり',
    hideAllHint: 'ボタン・計器・ウィンドウをすべて隠して、画面を見やすくします（H キー）',
    restoreAll: '表示を戻す',
    restoreAllHint: '隠したボタン・計器を、すべて出します（H キー）',
    fold: 'たたむ',
    foldHint: 'この操作を、小さなボタンにたたみます',
    unfold: '操作を出す',
    unfoldHint: '全画面の操作を開きます',
    names: {
      hud: '計器（HUD）',
      views: '視点の切り替え',
      playback: '再生の操作',
      legend: '区間の色分け',
      section: 'いまの区間',
      toolbar: 'ツールバー',
      left: 'コース・制御点',
      right: 'プロパティ',
      bottom: '解析',
      menu: '全画面の操作',
    },
  },
  rideControl: {
    group: '走行中の操作',
    accelerate: '加速',
    accelerateHint: '押している間、加速します（↑キーでも操作できます）',
    boost: 'ブースト',
    boostHint: (seconds: number, acceleration: string) =>
      `${seconds} 秒間、${acceleration} で急加速します（B キーでも操作できます）`,
    strength: '加速の強さ',
    idle: '押している間、加速します',
    accelerating: (acceleration: string) => `加速中（${acceleration}）`,
    boosting: 'ブースト中',
    blocked: (section: string) => `${section}では加速できません（設備の動きを優先）`,
    paused: '再生中に使えます',
  },
  summary: {
    title: '試算（仮のコース）',
    computing: '計算しています…',
    failed: '計算に失敗しました：',
    length: '全長',
    duration: '所要時間',
    maxSpeed: '最高速度',
    maxG: '最大上下 G',
  },
  disclaimer:
    '本アプリの安全性・快適性の評価はシミュレーション上の参考値であり、実在設備の安全性を示すものではありません。',
} as const;

/** 現在の文言セット。 */
export const messages = ja;
